import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart, BadgeCheck, MessageSquare } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const TIME_AGO = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function SwipesInterested() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    api.get("/swipe/interested")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  return (
    <div className="section-pad py-6 md:py-10" data-testid="swipes-interested-page">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5 mb-4" data-testid="interested-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="font-display font-bold text-2xl md:text-3xl text-slate-900 flex items-center gap-2">
        <Heart className="w-6 h-6 text-[#0654BA] fill-current" /> Interested in your items
      </h1>
      <p className="text-sm text-slate-500 mt-1.5 mb-6">People who swiped on your listings. Reach out to start a trade.</p>

      {loading ? (
        <div className="vyrti-card p-12 text-center text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="vyrti-card p-12 text-center max-w-lg">
          <p className="font-display font-bold text-lg text-slate-900 mb-1">Nobody yet</p>
          <p className="text-sm text-slate-500">When someone swipes right on your items, you&apos;ll see them here. Make sure your listings have great photos and clear &quot;wants&quot;.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {items.map((it, i) => (
            <div key={`${it.user.user_id}-${it.listing.id}-${i}`} className="vyrti-card p-4 flex items-center gap-3" data-testid={`interest-${it.user.user_id}-${it.listing.id}`}>
              <div className="flex -space-x-2 flex-shrink-0">
                {it.user.picture ? (
                  <img src={imgSrc(it.user.picture)} alt={it.user.name} className="w-10 h-10 rounded-full object-cover border-2 border-white" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-xs font-bold text-slate-700">{it.user.name?.[0]?.toUpperCase()}</div>
                )}
                {it.listing.images?.[0] && (
                  <img src={imgSrc(it.listing.images[0])} alt="" className="w-10 h-10 rounded-md object-cover border-2 border-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1">
                  {it.user.name}
                  {it.user.verified && <BadgeCheck className="w-3.5 h-3.5 text-[#0654BA]" />}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  liked your <Link to={`/listing/${it.listing.id}`} className="text-[#0654BA] font-semibold hover:underline">{it.listing.title}</Link>
                </p>
                <p className="text-[11px] text-slate-400">{TIME_AGO(it.swiped_at)}</p>
              </div>
              <Link to={`/profile/${it.user.user_id}`} className="text-[#0654BA] hover:bg-slate-100 p-2 rounded-md" title="View profile" data-testid={`profile-${it.user.user_id}`}>
                <MessageSquare className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
